import React from 'react';

import './StatusStepIndicator.css';

export type StepStatus = 'completed' | 'current' | 'error' | 'pending' | 'terminal';

interface StatusStepIndicatorProps {
  /**
   * The status of the step
   */
  status: StepStatus;
  /**
   * Whether to show the line extending upward
   */
  showLineUp?: boolean;
  /**
   * Whether to show the line extending downward
   */
  showLineDown?: boolean;
  /**
   * Optional icon to display in the center
   */
  icon?: React.ReactNode;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

/**
 * StatusStepIndicator component
 *
 * Displays a visual indicator for a step in the application status tracker.
 * Shows an icon in a circular border with optional connecting lines above and below.
 *
 * @example
 * <StatusStepIndicator status="completed" showLineDown icon={<SuccessIcon />} />
 * <StatusStepIndicator status="current" showLineUp showLineDown icon={<LocationIcon />} />
 * <StatusStepIndicator status="pending" showLineUp />
 */
export function StatusStepIndicator({
  status,
  showLineUp = false,
  showLineDown = false,
  icon,
  className = '',
  'data-testid': dataTestId,
}: StatusStepIndicatorProps) {
  const baseTestId = dataTestId ?? 'status-step-indicator';
  const getStatusLabel = () => {
    switch (status) {
      case 'completed':
        return 'Completed step';
      case 'current':
        return 'Current step';
      case 'error':
        return 'Step requires action';
      case 'pending':
        return 'Pending step';
      case 'terminal':
        return 'Application ended at this step';
      default:
        return 'Step';
    }
  };

  return (
    <div
      aria-label={getStatusLabel()}
      className={`status-step-indicator ${className}`}
      data-testid={baseTestId}
      role='img'>
      <div
        className='status-step-indicator__container'
        data-testid={`${baseTestId}-container`}>
        {showLineUp && (
          <div
            aria-hidden='true'
            className='status-step-indicator__line status-step-indicator__line--up'
            data-testid={`${baseTestId}-line-up`}
          />
        )}
        <div
          className={`status-step-indicator__circle status-step-indicator__circle--${status}`}
          data-testid={`${baseTestId}-circle`}>
          {icon && (
            <div
              aria-hidden='true'
              className='status-step-indicator__icon'
              data-testid={`${baseTestId}-icon`}>
              {icon}
            </div>
          )}
        </div>
        {showLineDown && (
          <div
            aria-hidden='true'
            className='status-step-indicator__line status-step-indicator__line--down'
            data-testid={`${baseTestId}-line-down`}
          />
        )}
      </div>
    </div>
  );
}

export default StatusStepIndicator;
